import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Text,
  Button,
  Badge,
  BlockStack,
  InlineStack,
  Spinner,
  Banner,
  Divider,
} from '@shopify/polaris';
import { useSite } from '../hooks/useSites';
import { useApplyCode, useRemoveCode, useDisconnectGoogle } from '../hooks/useAnalytics';
import { googleApi } from '../lib/api';
import { Layout } from '../components/Layout';
import { EcommerceEvents } from '../components/EcommerceEvents';

export function Site() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { data: site, isLoading, error, refetch } = useSite(siteId!);
  const applyCodeMutation = useApplyCode();
  const removeCodeMutation = useRemoveCode();
  const disconnectGoogleMutation = useDisconnectGoogle();

  if (isLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Spinner size="large" />
        </div>
      </Layout>
    );
  }

  if (error || !site) {
    return (
      <Layout>
        <BlockStack gap="400">
          <Text as="h1" variant="headingLg" fontWeight="bold">
            Site Settings
          </Text>
          <Banner tone="critical">
            Failed to load site details.
          </Banner>
        </BlockStack>
      </Layout>
    );
  }

  const handleConnectGoogle = () => {
    window.location.href = googleApi.getConnectUrl(siteId!);
  };

  const handleDisconnectGoogle = async () => {
    if (confirm('Are you sure you want to disconnect Google Analytics?')) {
      await disconnectGoogleMutation.mutateAsync(siteId!);
      refetch();
    }
  };

  const handleApplyCode = async () => {
    await applyCodeMutation.mutateAsync(siteId!);
    refetch();
  };

  const handleRemoveCode = async () => {
    if (confirm('Are you sure you want to remove the tracking code?')) {
      await removeCodeMutation.mutateAsync(siteId!);
      refetch();
    }
  };

  return (
    <Layout>
      <BlockStack gap="400">
        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text as="h1" variant="headingLg" fontWeight="bold">
              {site.display_name}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {site.short_name}
            </Text>
          </div>
          {site.google_connected && site.analytics?.property_id && (
            <Button variant="primary" onClick={() => navigate(`/site/${siteId}/standard-reports`)}>
              View Reports
            </Button>
          )}
        </div>
        {/* Google Connection */}
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Google Analytics Connection
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Connect your Google account to access GA4 data
                </Text>
              </BlockStack>
              {site.google_connected ? (
                <Badge tone="success">Connected</Badge>
              ) : (
                <Badge tone="attention">Not Connected</Badge>
              )}
            </InlineStack>

            {site.google_connected && site.google_email && (
              <Text as="p" variant="bodySm">
                Connected as: <strong>{site.google_email}</strong>
              </Text>
            )}

            <InlineStack gap="200">
              {site.google_connected ? (
                <>
                  <Button onClick={() => navigate(`/site/${siteId}/setup`)}>
                    Change Property
                  </Button>
                  <Button
                    variant="plain"
                    tone="critical"
                    loading={disconnectGoogleMutation.isPending}
                    onClick={handleDisconnectGoogle}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button variant="primary" onClick={handleConnectGoogle}>
                  Connect Google Account
                </Button>
              )}
            </InlineStack>
          </BlockStack>
        </Card>

        {/* GA4 Property - Only show when Google is connected */}
        {site.google_connected && site.analytics && (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    GA4 Property
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Your connected Google Analytics 4 property
                  </Text>
                </BlockStack>
                <Badge tone="success">Enabled</Badge>
              </InlineStack>

              {site.analytics.property_id && (
                <BlockStack gap="200">
                  <Text as="p" variant="bodySm">
                    Property: <strong>{site.analytics.property_id}</strong>
                  </Text>
                  {site.analytics.measurement_id && (
                    <Text as="p" variant="bodySm">
                      Measurement ID: <strong>{site.analytics.measurement_id}</strong>
                    </Text>
                  )}
                  {site.analytics.data_stream_name && (
                    <Text as="p" variant="bodySm">
                      Data Stream: <strong>{site.analytics.data_stream_name}</strong>
                    </Text>
                  )}
                </BlockStack>
              )}

              <Button onClick={() => navigate(`/site/${siteId}/setup`)}>
                Change Property
              </Button>
            </BlockStack>
          </Card>
        )}

        {/* GA4 Property Warning - Show when Google is disconnected but analytics was configured */}
        {!site.google_connected && site.analytics?.property_id && (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    GA4 Property
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Your Google Analytics 4 property configuration
                  </Text>
                </BlockStack>
                <Badge tone="critical">Disconnected</Badge>
              </InlineStack>

              <Banner tone="warning">
                Google account is disconnected. Connect your Google account above to access GA4 data and reports.
              </Banner>

              <BlockStack gap="200">
                <Text as="p" variant="bodySm" tone="subdued">
                  Previously configured property:
                </Text>
                {site.analytics.measurement_id && (
                  <Text as="p" variant="bodySm">
                    Measurement ID: <strong>{site.analytics.measurement_id}</strong>
                  </Text>
                )}
                {site.analytics.data_stream_name && (
                  <Text as="p" variant="bodySm">
                    Data Stream: <strong>{site.analytics.data_stream_name}</strong>
                  </Text>
                )}
              </BlockStack>
            </BlockStack>
          </Card>
        )}

        {/* Tracking Code */}
        {site.analytics?.measurement_id && (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    Tracking Code
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    GA4 tracking code injection to your Webflow site
                  </Text>
                </BlockStack>
                {site.analytics.custom_code_applied ? (
                  <Badge tone="success">Applied</Badge>
                ) : (
                  <Badge tone="warning">Not Applied</Badge>
                )}
              </InlineStack>

              <InlineStack gap="200">
                {site.analytics.custom_code_applied ? (
                  <Button
                    tone="critical"
                    loading={removeCodeMutation.isPending}
                    onClick={handleRemoveCode}
                  >
                    Remove Tracking Code
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    loading={applyCodeMutation.isPending}
                    onClick={handleApplyCode}
                  >
                    Apply Tracking Code
                  </Button>
                )}
              </InlineStack>

              {applyCodeMutation.isSuccess && (
                <Banner tone="success" onDismiss={() => applyCodeMutation.reset()}>
                  {applyCodeMutation.data?.published
                    ? 'Tracking code applied and site published successfully!'
                    : 'Tracking code applied! Don\'t forget to publish your Webflow site.'}
                </Banner>
              )}

              {applyCodeMutation.isError && (
                <Banner tone="critical" onDismiss={() => applyCodeMutation.reset()}>
                  Failed to apply tracking code. Please try again.
                </Banner>
              )}
            </BlockStack>
          </Card>
        )}

        {/* E-commerce Events */}
        {site.analytics?.measurement_id && (
          <EcommerceEvents
            siteId={siteId!}
            isConfigured={!!site.analytics?.property_id}
          />
        )}

        <Divider />

        {/* Quick Actions */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Quick Actions
            </Text>
            <InlineStack gap="200">
              <Button url={`https://${site.short_name}.webflow.io`} external>
                Preview Site
              </Button>
              <Button
                url={`https://webflow.com/dashboard/sites/${site.webflow_site_id}`}
                external
              >
                Open in Webflow
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Layout>
  );
}
